import React from 'react';
import { useTheme } from '@mui/material';
import {
  Grid,
  Card,
  Paper,
  CardActions,
  Button,
  CardContent,
  CardHeader,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { CcfTextField } from '@nice-ccf/ui-controls';
import { DigitalChannelType, MediaType } from '@nice-ccf/core-sdk';
import { Contact } from './simulator.slice';
import DropDown from './drop-down';
import simulatorStyles from './simulator.styles';
import classes from './simulator.module.scss';

interface ContactDetailCardProps {
  selectedContact: Contact;
  handleInputChange: (field: string, value: any) => void;
  buttonText: string;
  handleButtonClick: () => void;
}

const CHANNEL_OPTIONS = [
  'Email',
  'Instagram',
  'X',
  'Facebook',
  'Linkedin',
  'Chat',
];

const CONTACT_STATUS_OPTIONS = ['ringing', 'on hold', 'conference'];

const AGENT_ASSIST_STATUS_OPTIONS = [
  'Negative Score',
  'Intermediate Score',
  'Positive Score',
];

const RTA_STATUS_OPTIONS = [
  'Authenticated',
  'Missing Patron ID',
  'Not Authenticated',
];

const MEDIA_OPTIONS = ['Digital', 'Voice', 'Voicemail', 'VideoCall'];

const COPILOT_SOURCE_OPTIONS = ['JSON', 'Live LLM'];

// Base text field configuration for reuse
const baseTextFieldProps = {
  className: classes.input,
  variant: 'outlined' as const,
  InputLabelProps: { shrink: true },
};

// Component for standard text input fields
/**
 * To show the contact text field
 * @param props - it will take text field props, and onchange handler
 * @returns
 * @example
 */
const ContactTextField = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string | number;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <CcfTextField
    {...baseTextFieldProps}
    id={`sm_contactDetails_${id}`}
    label={label}
    value={value}
    onChange={onChange}
  />
);

// Component for copilot switch
/**
 * To show the copilot switch field,
 * @param props - it will take text field props, and onchange handler
 * @returns
 * @example
 */
const CopilotSwitch = ({
  value,
  onChange,
  simStyles,
}: {
  value: string;
  onChange: (checked: boolean) => void;
  simStyles: any;
}) => (
  <FormControlLabel
    label="Activate Copilot"
    labelPlacement="start"
    style={simStyles.simLabelStyles}
    control={
      <Switch
        name="copilotAssist"
        color="primary"
        checked={value === 'Yes'}
        sx={{ ' .MuiFormControlLabel-label': { width: 165 } }}
        style={{ marginLeft: 0 }}
        onChange={(event) => onChange(event.target.checked)}
      />
    }
  />
);

// Digital contact specific fields
/**
 * To show the digital contact field
 * @param props - it will take text field props, and onchange handler
 * @returns
 * @example
 */
const DigitalContactFields = ({
  selectedContact,
  handleInputChange,
  simStyles,
}: {
  selectedContact: Contact;
  handleInputChange: (field: string, value: any) => void;
  simStyles: any;
}) => (
  <>
    <DropDown
      currentValue={selectedContact.channel || ''}
      field="channel"
      label="Channel"
      options={CHANNEL_OPTIONS}
      handleInputChange={handleInputChange}
      id={`sm_contactDetails_channel_${selectedContact.channel}`}
      stackName="contactDetails_channel"
    />
    <CopilotSwitch
      value={selectedContact.copilotAssist || ''}
      onChange={(checked) =>
        handleInputChange('copilotAssist', checked ? 'Yes' : 'No')
      }
      simStyles={simStyles}
    />
    {selectedContact.channel === DigitalChannelType.CHAT &&
      selectedContact.copilotAssist === 'Yes' && (
        <CopilotSourceFields
          selectedContact={selectedContact}
          handleInputChange={handleInputChange}
          simStyles={simStyles}
        />
      )}
  </>
);

// Voice contact specific fields
/**
 * To show the voice contact text fields
 * @param props - it will take text field props, and onchange handler
 * @returns
 * @example
 */
const VoiceContactFields = ({
  selectedContact,
  handleInputChange,
  simStyles,
}: {
  selectedContact: Contact;
  handleInputChange: (field: string, value: any) => void;
  simStyles: any;
}) => (
  <>
    <DropDown
      currentValue={selectedContact.contactStatus || ''}
      field="contactStatus"
      label="Contact Status"
      options={CONTACT_STATUS_OPTIONS}
      handleInputChange={handleInputChange}
      id={`sm_contactDetails_contactStatus_${selectedContact.contactStatus}`}
      stackName="contactDetails_contactStatus"
    />
    <CopilotSwitch
      value={selectedContact.copilotAssist || ''}
      onChange={(checked) =>
        handleInputChange('copilotAssist', checked ? 'Yes' : 'No')
      }
      simStyles={simStyles}
    />
    {selectedContact.copilotAssist === 'Yes' && (
      <CopilotSourceFields
        selectedContact={selectedContact}
        handleInputChange={handleInputChange}
        simStyles={simStyles}
      />
    )}
    <DropDown
      currentValue={selectedContact.rtaStatus || ''}
      field="rtaStatus"
      label="RTA Status"
      options={RTA_STATUS_OPTIONS}
      handleInputChange={handleInputChange}
      id={`sm_contactDetails_rtaStatus_${selectedContact.rtaStatus}`}
      stackName="contactDetails_rtaStatus"
    />
    <DropDown
      currentValue={selectedContact.agentAssistStatus || ''}
      field="agentAssistStatus"
      label="Agent Assist Status"
      options={AGENT_ASSIST_STATUS_OPTIONS}
      handleInputChange={handleInputChange}
      id={`sm_contactDetails_agentAssistStatus_${selectedContact.agentAssistStatus}`}
      stackName="contactDetails_agentAssistStatus"
    />
  </>
);

// Copilot source related fields
/**
 * To show the copilot source related fields
 * @param props - it will take selecte contact props, and onchange handler
 * @returns
 * @example
 */
const CopilotSourceFields = ({
  selectedContact,
  handleInputChange,
  simStyles,
}: {
  selectedContact: Contact;
  handleInputChange: (field: string, value: any) => void;
  simStyles: any;
}) => (
  <>
    <DropDown
      currentValue={selectedContact?.copilotSource || ''}
      field="copilotSource"
      label="Copilot Source"
      options={COPILOT_SOURCE_OPTIONS}
      handleInputChange={handleInputChange}
      id={`sm_contactDetails_copilotSource_${selectedContact.copilotSource}`}
      stackName="contactDetails_copilotSource"
    />
    {selectedContact?.copilotSource === 'JSON' &&
      selectedContact.media === MediaType.DIGITAL && (
        <FormControlLabel
          label="Bot Transcript"
          labelPlacement="start"
          style={simStyles.simLabelStyles}
          control={
            <Switch
              id="sm_contactDetails_botTranscript"
              name="BotTranscript"
              color="primary"
              checked={selectedContact?.botTranscript}
              sx={{ ' .MuiFormControlLabel-label': { width: 165 } }}
              style={{ marginLeft: 0 }}
              onChange={(e) =>
                handleInputChange('botTranscript', e.target.checked)
              }
            />
          }
        />
      )}
    {selectedContact?.copilotSource === 'Live LLM' &&
      selectedContact.media !== MediaType.DIGITAL && (
        <FormControlLabel
          label="Live Voice"
          labelPlacement="start"
          style={simStyles.simLabelStyles}
          control={
            <Switch
              name="liveVoice"
              color="primary"
              checked={selectedContact.liveVoice === 'Yes'}
              sx={{ ' .MuiFormControlLabel-label': { width: 165 } }}
              style={{ marginLeft: 0 }}
              onChange={(e) =>
                handleInputChange('liveVoice', e.target.checked ? 'Yes' : 'No')
              }
            />
          }
        />
      )}
  </>
);

/**
 * To show the contact detail for the selected contact from the contact list card. This will also let user to edit the values for the contact detail
 * @param props - it will take selected contact detail, and onchange handler
 * @returns
 * @example
 */
const ContactDetailCard: React.FC<ContactDetailCardProps> = (props) => {
  const { selectedContact, handleInputChange, buttonText, handleButtonClick } =
    props;
  const theme = useTheme();
  const simStyles = simulatorStyles(theme);
  return (
    <Grid className={classes.simulatorColumn}>
      <Paper elevation={3} className={classes.simulatorPaperWithFullheight}>
        <Card className={classes.heightInherit}>
          <CardHeader title="Contact Details" />
          <CardContent>
            <form
              className={classes.root + ' ' + classes.simulatorForm}
              noValidate
              autoComplete="off"
            >
              <CcfTextField
                id="sm_contactDetails_id"
                className={classes.input}
                label="ID"
                variant="outlined"
                value={selectedContact.id}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('id', event?.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <CcfTextField
                className={classes.input}
                id="sm_contactDetails_messageType"
                label="Message Type"
                variant="outlined"
                value={selectedContact.messageType}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('messageType', event?.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <CcfTextField
                className={classes.input}
                id="sm_contactDetails_queueName"
                label="Queue Name"
                variant="outlined"
                value={selectedContact.queueName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('queueName', event?.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <DropDown
                {...{
                  currentValue: selectedContact.media || '',
                  field: 'media',
                  label: 'Media',
                  options: ['Digital', 'Voice', 'Voicemail', 'VideoCall'],
                  handleInputChange,
                  id: `sm_contactDetails_media_${selectedContact.media}`,
                  stackName: 'contactDetails_media',
                }}
              />
              {selectedContact.media === MediaType.DIGITAL ? (
                <>
                  <DropDown
                    {...{
                      currentValue: selectedContact.channel || '',
                      field: 'channel',
                      label: 'Channel',
                      options: [
                        'Email',
                        'Instagram',
                        'X',
                        'Facebook',
                        'Linkedin',
                        'Chat',
                      ],
                      handleInputChange,
                      id: `sm_contactDetails_channel_${selectedContact.channel}`,
                      stackName: 'contactDetails_channel',
                    }}
                  />
                  {/* {
                    <DropDown
                      {...{
                        currentValue: selectedContact.copilotAssist || '',
                        field: 'copilotAssist',
                        label: 'Copilot Assist',
                        options: ['Yes', 'No'],
                        handleInputChange,
                        id: `sm_contactDetails_copilotAssist_${selectedContact.copilotAssist}`,
                        stackName: 'contactDetails_copilotAssist',
                      }}
                    />
                  } */}
                  {
                    <FormControlLabel
                      label="Activate Copilot"
                      labelPlacement="start"
                      style={simStyles.simLabelStyles}
                      control={
                        <Switch
                          name="copilotAssist"
                          color="primary"
                          checked={
                            selectedContact.copilotAssist === 'Yes'
                              ? true
                              : false
                          }
                          sx={{
                            ' .MuiFormControlLabel-label': {
                              width: 165,
                            },
                          }}
                          style={{ marginLeft: 0 }}
                          onChange={(event) =>
                            handleInputChange(
                              'copilotAssist',
                              event?.target.checked === true ? 'Yes' : 'No'
                            )
                          }
                        />
                      }
                    />
                  }
                  {selectedContact.media === MediaType.DIGITAL &&
                    (selectedContact.channel === DigitalChannelType.CHAT ||
                      selectedContact.channel === DigitalChannelType.EMAIL) &&
                    selectedContact.copilotAssist === 'Yes' && (
                      <>
                        <DropDown
                          {...{
                            currentValue: selectedContact?.copilotSource || '',
                            field: 'copilotSource',
                            label: 'Copilot Source',
                            options: ['JSON', 'Live LLM'],
                            handleInputChange,
                            id: `sm_contactDetails_copilotSource_${selectedContact.copilotSource}`,
                            stackName: 'contactDetails_copilotSource',
                          }}
                        />
                        {selectedContact?.copilotSource === 'JSON' && (
                          <FormControlLabel
                            label="Bot Transcript"
                            labelPlacement="start"
                            style={simStyles.simLabelStyles}
                            control={
                              <Switch
                                id="sm_contactDetails_botTranscript"
                                name="BotTranscript"
                                color="primary"
                                checked={selectedContact?.botTranscript}
                                sx={{
                                  ' .MuiFormControlLabel-label': {
                                    width: 165,
                                  },
                                }}
                                style={{ marginLeft: 0 }}
                                onChange={(e) =>
                                  handleInputChange(
                                    'botTranscript',
                                    e.target.checked
                                  )
                                }
                              />
                            }
                          />
                        )}
                      </>
                    )}
                </>
              ) : (
                <>
                  <DropDown
                    {...{
                      currentValue: selectedContact.contactStatus || '',
                      field: 'contactStatus',
                      label: 'Contact Status',
                      options: ['ringing', 'on hold', 'conference'],
                      handleInputChange,
                      id: `sm_contactDetails_contactStatus_${selectedContact.contactStatus}`,
                      stackName: 'contactDetails_contactStatus',
                    }}
                  />
                  {/* <DropDown
                    {...{
                      currentValue: selectedContact.copilotAssist || '',
                      field: 'copilotAssist',
                      label: 'Copilot Assist',
                      options: ['Yes', 'No'],
                      handleInputChange,
                      id: `sm_contactDetails_copilotAssist_${selectedContact.copilotAssist}`,
                      stackName: 'contactDetails_copilotAssist',
                    }}
                  /> */}
                  {
                    <FormControlLabel
                      label="Activate Copilot"
                      labelPlacement="start"
                      style={simStyles.simLabelStyles}
                      control={
                        <Switch
                          name="copilotAssist"
                          color="primary"
                          checked={
                            selectedContact.copilotAssist === 'Yes'
                              ? true
                              : false
                          }
                          sx={{
                            ' .MuiFormControlLabel-label': {
                              width: 165,
                            },
                          }}
                          style={{ marginLeft: 0 }}
                          onChange={(event) =>
                            handleInputChange(
                              'copilotAssist',
                              event?.target.checked === true ? 'Yes' : 'No'
                            )
                          }
                        />
                      }
                    />
                  }
                  {selectedContact.copilotAssist === 'Yes' && (
                    <DropDown
                      {...{
                        currentValue: selectedContact?.copilotSource || '',
                        field: 'copilotSource',
                        label: 'Copilot Source',
                        options: ['JSON', 'Live LLM'],
                        handleInputChange,
                        id: `sm_contactDetails_copilotSource_${selectedContact.copilotSource}`,
                        stackName: 'contactDetails_copilotSource',
                      }}
                    />
                  )}

                  {selectedContact?.copilotSource === 'Live LLM' && (
                    <FormControlLabel
                      label="Live Voice"
                      labelPlacement="start"
                      style={simStyles.simLabelStyles}
                      control={
                        <Switch
                          name="liveVoice"
                          color="primary"
                          checked={
                            selectedContact.liveVoice === 'Yes' ? true : false
                          }
                          sx={{
                            ' .MuiFormControlLabel-label': {
                              width: 165,
                            },
                          }}
                          style={{ marginLeft: 0 }}
                          onChange={(event) =>
                            handleInputChange(
                              'liveVoice',
                              event?.target.checked === true ? 'Yes' : 'No'
                            )
                          }
                        />
                      }
                    />
                  )}

                  <DropDown
                    {...{
                      currentValue: selectedContact.rtaStatus || '',
                      field: 'rtaStatus',
                      label: 'RTA Status',
                      options: [
                        'Authenticated',
                        'Missing Patron ID',
                        'Not Authenticated',
                      ],
                      handleInputChange,
                      id: `sm_contactDetails_rtaStatus_${selectedContact.rtaStatus}`,
                      stackName: 'contactDetails_rtaStatus',
                    }}
                  />
                  <DropDown
                    {...{
                      currentValue: selectedContact.agentAssistStatus || '',
                      field: 'agentAssistStatus',
                      label: 'Agent Assist Status',
                      options: [
                        'Negative Score',
                        'Intermediate Score',
                        'Positive Score',
                      ],
                      handleInputChange,
                      id: `sm_contactDetails_agentAssistStatus_${selectedContact.agentAssistStatus}`,
                      stackName: 'contactDetails_agentAssistStatus',
                    }}
                  />
                </>
              )}
              {/* {selectedContact.copilotAssist === 'Yes' && (
                <>
                  <FormControlLabel
                    label="Journey History"
                    labelPlacement="start"
                    style={simStyles.simLabelStyles}
                    control={
                      <Switch
                        id="sm_contactDetails_journeyHistory"
                        name="journeyHistory"
                        color="primary"
                        checked={selectedContact?.journeyHistory}
                        sx={{
                          ' .MuiFormControlLabel-label': {
                            width: 165,
                          },
                        }}
                        style={{ marginLeft: 0 }}
                        onChange={(e) =>
                          handleInputChange('journeyHistory', e.target.checked)
                        }
                      />
                    }
                  />
                  <FormControlLabel
                    label="Agent Transfer Summary"
                    labelPlacement="start"
                    style={simStyles.simLabelStyles}
                    control={
                      <Switch
                        id="sm_contactDetails_showtransferSummary"
                        name="showtransferSummary"
                        color="primary"
                        checked={selectedContact?.showtransferSummary}
                        sx={{
                          ' .MuiFormControlLabel-label': {
                            width: 165,
                          },
                        }}
                        style={{ marginLeft: 0 }}
                        onChange={(e) =>
                          handleInputChange(
                            'showtransferSummary',
                            e.target.checked
                          )
                        }
                      />
                    }
                  />
                </>
              )} */}

              <CcfTextField
                className={classes.input}
                id={`sm_contactDetails_crmLabel`}
                label="CRM Label"
                variant="outlined"
                value={selectedContact.crmLabel}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('crmLabel', event?.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <CcfTextField
                className={classes.input}
                id={`sm_contactDetails_crmUrl`}
                label="CRM URL"
                variant="outlined"
                value={selectedContact.crmUrl}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleInputChange('crmUrl', event?.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </form>
          </CardContent>
          <CardActions>
            <Button
              id={`sm_contactDetails_button`}
              size="large"
              variant="contained"
              color="primary"
              className={classes.simulatorButton}
              onClick={handleButtonClick}
            >
              {buttonText}
            </Button>
          </CardActions>
        </Card>
      </Paper>
    </Grid>
  );
};

export default ContactDetailCard;
